import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { booksApi, usersApi, discoveryApi } from '@/lib/api';
import AppNavbar from '@/components/app/AppNavbar';
import BookCard from '@/components/app/BookCard';
import BookAutocomplete from '@/components/app/BookAutocomplete';
import UserCard from '@/components/app/UserCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Search, BookOpen, Users, Compass, PenLine, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import MobileBottomNav from '@/components/app/MobileBottomNav';

// ─── Manual Entry Dialog ───────────────────────────────────────────────────

function ManualBookDialog({ open, onClose, onCreated, initialTitle = '' }) {
  const [title, setTitle] = useState(initialTitle);
  const [author, setAuthor] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Sync initialTitle when dialog opens
  useEffect(() => { if (open) setTitle(initialTitle); }, [open, initialTitle]);

  const reset = () => {
    setTitle('');
    setAuthor('');
    setCoverUrl('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (!author.trim()) { toast.error('Author is required'); return; }
    setSubmitting(true);
    try {
      const book = await booksApi.createManual({
        title: title.trim(),
        author: author.trim(),
        coverUrl: coverUrl.trim(),
      });
      toast.success('Book added!');
      reset();
      onCreated(book);
    } catch (err) {
      toast.error(err.message || 'Failed to add book');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-cream border-cream-dark max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-ink">Add Book Manually</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Cover preview + URL input */}
          <div className="flex items-start gap-4">
            <div className="w-20 flex-shrink-0 aspect-[2/3] rounded-lg overflow-hidden bg-cream-dark border border-cream-dark flex items-center justify-center">
              {coverUrl.trim() ? (
                <img
                  src={coverUrl.trim()}
                  alt="cover preview"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <BookOpen className="w-6 h-6 text-ink-muted opacity-50" />
              )}
            </div>
            <div className="flex-1 space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="manual-title" className="text-ink-light text-sm font-medium">Title *</Label>
                <Input
                  id="manual-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Book title"
                  className="bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-author" className="text-ink-light text-sm font-medium">Author *</Label>
                <Input
                  id="manual-author"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="Author name"
                  className="bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted text-sm"
                />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="manual-cover-url" className="text-ink-light text-sm font-medium">Cover image URL <span className="text-ink-muted font-normal">(optional)</span></Label>
            <Input
              id="manual-cover-url"
              value={coverUrl}
              onChange={(e) => setCoverUrl(e.target.value)}
              placeholder="https://..."
              className="bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted text-sm"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} className="text-ink-muted">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !title.trim() || !author.trim()}
            className="bg-terra hover:bg-terra/90 text-cream"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Add Book
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────

export default function SearchPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';

  const [query, setQuery] = useState(initialQuery);
  const [activeTab, setActiveTab] = useState('books');
  const [peopleQuery, setPeopleQuery] = useState(initialQuery);
  const [userResults, setUserResults] = useState([]);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [discoverBooks, setDiscoverBooks] = useState([]);
  const [discoverUsers, setDiscoverUsers] = useState([]);
  const [loadingDiscovery, setLoadingDiscovery] = useState(true);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualInitialTitle, setManualInitialTitle] = useState('');

  const debounceRef = useRef(null);

  useEffect(() => {
    const fetchDiscovery = async () => {
      try {
        const [books, users] = await Promise.all([
          discoveryApi.getBooks(),
          discoveryApi.getUsers(),
        ]);
        setDiscoverBooks(Array.isArray(books) ? books : []);
        setDiscoverUsers(Array.isArray(users) ? users : []);
      } catch (_) {
      } finally {
        setLoadingDiscovery(false);
      }
    };
    fetchDiscovery();
  }, []);

  const searchPeople = useCallback(async (q) => {
    if (!q.trim()) { setUserResults([]); return; }
    setLoadingPeople(true);
    try {
      const results = await usersApi.searchUsers(q);
      setUserResults(results);
    } catch (_) {
      setUserResults([]);
    } finally {
      setLoadingPeople(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchPeople(peopleQuery), 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [peopleQuery, searchPeople]);

  const handleBookSelect = async (book) => {
    try {
      const stored = await booksApi.getOrCreate(book);
      navigate(`/books/${stored.id}`);
    } catch {
      navigate(`/books/google/${book.google_books_id}`);
    }
  };

  const handleManualAdd = (title = '') => {
    setManualInitialTitle(title);
    setManualDialogOpen(true);
  };

  const handleManualCreated = (book) => {
    setManualDialogOpen(false);
    navigate(`/books/${book.id}`);
  };

  const hasQuery = query.trim().length > 0;

  return (
    <div className="min-h-screen bg-cream pb-20 md:pb-0">
      <AppNavbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Compass className="w-5 h-5 text-terra" />
            <h1 className="font-heading text-2xl text-ink">Discover</h1>
          </div>

          {/* Tab switcher */}
          <div className="flex gap-2 mb-4">
            <Button
              size="sm"
              onClick={() => setActiveTab('books')}
              className={activeTab === 'books'
                ? 'bg-terra text-cream hover:bg-terra/90'
                : 'bg-cream-dark text-ink hover:bg-cream-dark/80 border border-cream-dark'}
            >
              <BookOpen className="w-4 h-4 mr-1.5" />Books
            </Button>
            <Button
              size="sm"
              onClick={() => setActiveTab('people')}
              className={activeTab === 'people'
                ? 'bg-terra text-cream hover:bg-terra/90'
                : 'bg-cream-dark text-ink hover:bg-cream-dark/80 border border-cream-dark'}
            >
              <Users className="w-4 h-4 mr-1.5" />People
            </Button>
          </div>

          {/* Books: autocomplete */}
          {activeTab === 'books' && (
            <BookAutocomplete
              onSelect={handleBookSelect}
              onManualAdd={handleManualAdd}
              onSearch={() => {}}
              placeholder="Search Google Books + Open Library..."
              className="max-w-xl"
              autoFocus
            />
          )}

          {/* People: plain search */}
          {activeTab === 'people' && (
            <div className="relative max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
              <Input
                type="text"
                placeholder="Search by name or username..."
                value={peopleQuery}
                onChange={(e) => setPeopleQuery(e.target.value)}
                className="pl-9 bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted"
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Content */}
        {activeTab === 'books' && (
          <div className="space-y-6">
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-terra" />
                  <h2 className="font-heading text-xl text-ink">Popular Books</h2>
                </div>
                {user && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleManualAdd('')}
                    className="border-cream-dark text-ink-muted hover:border-terra hover:text-terra text-xs gap-1.5"
                  >
                    <PenLine className="w-3.5 h-3.5" />
                    Add manually
                  </Button>
                )}
              </div>
              {loadingDiscovery ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="space-y-2">
                      <Skeleton className="aspect-[2/3] rounded-lg bg-cream-dark" />
                      <Skeleton className="h-3 w-full bg-cream-dark" />
                    </div>
                  ))}
                </div>
              ) : discoverBooks.length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
                  {discoverBooks.map((book) => (
                    <motion.div key={book.id || book.google_books_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                      <BookCard book={book} />
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-ink-muted text-sm text-center py-8">Search for a book above to get started.</p>
              )}
            </section>
          </div>
        )}

        {activeTab === 'people' && (
          <div className="space-y-6">
            {loadingPeople ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl bg-cream-dark" />)}
              </div>
            ) : peopleQuery.trim() && userResults.length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-12 h-12 text-ink-muted mx-auto mb-4 opacity-40" />
                <p className="text-ink-muted">No readers found for &ldquo;{peopleQuery}&rdquo;</p>
              </div>
            ) : userResults.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {userResults.map((u) => (
                  <div key={u.username} className="bg-cream border border-cream-dark rounded-xl">
                    <UserCard user={u} />
                  </div>
                ))}
              </div>
            ) : (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-terra" />
                  <h2 className="font-heading text-xl text-ink">Readers to Discover</h2>
                </div>
                {loadingDiscovery ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl bg-cream-dark" />)}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {discoverUsers.map((u) => (
                      <div key={u.username} className="bg-cream border border-cream-dark rounded-xl">
                        <UserCard user={u} />
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}
          </div>
        )}
      </div>

      <ManualBookDialog
        open={manualDialogOpen}
        onClose={() => setManualDialogOpen(false)}
        onCreated={handleManualCreated}
        initialTitle={manualInitialTitle}
      />

      <MobileBottomNav />
    </div>
  );
}
